import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  List,
  Card,
  Stack,
  Select,
  Avatar,
  Button,
  ListItem,
  MenuItem,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  ListItemAvatar,
  InputAdornment,
} from '@mui/material';

import { useConfirmModal } from 'src/hooks/useModal';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import getDisplayName from 'src/utils/getDisplayName';

import Iconify from 'src/components/iconify';

import { scrollbarStyle } from 'src/sections/style';
import {
  cardTicketModalStyle,
  listTicketModalStyle,
  listItemTicketPermissionModalStyle,
} from 'src/sections/ticketDetails/styles';

import updatePermissionListAfterAdding from '../utils/updatePermissionListAfterAdding';

AddUsersPermissionsForm.propTypes = {
  usersList: PropTypes.array,
  ticketId: PropTypes.string,
  ticketUsersIds: PropTypes.array,
  setPermissionUsers: PropTypes.func,
  permissionUsers: PropTypes.array,
  addMultiplePermissions: PropTypes.func,
  availableUserIds: PropTypes.object,
  hideModal: PropTypes.func,
  isModal: PropTypes.bool, // New prop to differentiate between modal and non-modal usage
};

export default function AddUsersPermissionsForm({
  usersList,
  ticketId,
  ticketUsersIds,
  setPermissionUsers,
  permissionUsers,
  addMultiplePermissions,
  availableUserIds,
  hideModal,
  isModal = false, // Default to false for backward compatibility
}) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedPermission, setSelectedPermission] = useState('read');
  const [searchValue, setSearchValue] = useState('');
  const [isNotFound, setIsNotFound] = useState(false);
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const handleFilterByName = (event) => {
    setSearchValue(event.target.value);
  };

  const filteredUsers = useMemo(() => {
    const lowerCaseSearchValue = searchValue.toLowerCase();
    const availableUsers = usersList.filter((user) => !ticketUsersIds.includes(user.id));

    const filteredData = availableUsers.filter(
      (user) =>
        getDisplayName(user).toLowerCase().includes(lowerCaseSearchValue) ||
        (user.email || '').toLowerCase().includes(lowerCaseSearchValue)
    );

    setIsNotFound(!filteredData.length && !!searchValue);
    return filteredData;
  }, [ticketUsersIds, usersList, searchValue]);

  const { showConfirmModal } = useConfirmModal(
    t('pages.ticketDetails.confirmPermission'),
    t('pages.ticketDetails.areYouSureToAdd'),
    async () => {
      const successCallback = () => {
        // Update users list
        const updatedUsers = updatePermissionListAfterAdding({
          users: selectedUsers,
          permission: selectedPermission,
          availableUserIds,
          permissionUsers,
        });
        setPermissionUsers(updatedUsers);
      };
      // Call API to add multiple users
      addMultiplePermissions(
        ticketId,
        selectedUsers.flatMap((user) => user.id),
        selectedPermission === 'read',
        selectedPermission === 'write',
        successCallback
      );
    }
  );

  const handlePermissionChange = (event) => {
    setSelectedPermission(event.target.value);
  };

  const handleUserSelect = (user) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((item) => item.id === user.id);
      if (isSelected) {
        return prev.filter((item) => item.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const handleAddPermissionClick = () => {
    if (isModal) {
      // Logic for modal version
      selectedUsers.forEach((user) => {
        if (selectedPermission === 'read') {
          user.permissions = [true, false];
          user.default = false;
        } else {
          user.permissions = [true, true];
          user.default = false;
        }
      });
      setPermissionUsers((prev) => [...selectedUsers, ...prev]);
      hideModal();
    } else {
      // Logic for non-modal version
      showConfirmModal();
    }
  };

  return (
    <Stack px={2} spacing={2} my={2}>
      <TextField
        value={searchValue}
        onChange={handleFilterByName}
        placeholder={`${t('filters.search')}`}
        size={isSmallScreen ? 'small' : 'medium'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify
                icon="eva:search-fill"
                sx={{
                  color: 'text.disabled',
                  width: isSmallScreen ? 16 : 20,
                  height: isSmallScreen ? 16 : 20,
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          '& input': {
            fontSize: isSmallScreen ? '0.775rem' : '1rem',
          },
        }}
      />
      <Card
        sx={{
          ...cardTicketModalStyle,
          height: isSmallScreen ? '200px' : '350px',
        }}
      >
        <List sx={listTicketModalStyle}>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => {
              const { id, email, avatar, departmentName } = user;
              const username = getDisplayName(user);

              return (
                <ListItem
                  divider
                  key={id}
                  onClick={() => handleUserSelect(user)}
                  sx={listItemTicketPermissionModalStyle(selectedUsers, user)}
                  alignItems="flex-start"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Stack direction="row">
                      <ListItemAvatar>
                        <Avatar
                          alt=""
                          src={avatar}
                          sx={{
                            width: isSmallScreen ? 30 : 40,
                            height: isSmallScreen ? 30 : 40,
                          }}
                        />
                      </ListItemAvatar>

                      <Stack spacing={0.5}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: isSmallScreen ? '0.65rem' : '0.85rem',
                          }}
                        >
                          <b>{username}</b>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: isSmallScreen ? '0.65rem' : '0.85rem',
                          }}
                        >
                          <b>{t('attributes.email')}: </b>
                          {email}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: isSmallScreen ? '0.65rem' : '0.85rem',
                          }}
                        >
                          <b>{t('tableHeads.user.role')}: </b>
                          {t(`roles.${user.appUserRole}`)}
                        </Typography>
                        {departmentName !== 'none' && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: isSmallScreen ? '0.65rem' : '0.85rem',
                            }}
                          >
                            <b>{t('attributes.department')}: </b>
                            {departmentName}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </ListItem>
              );
            })
          ) : (
            <Typography
              textAlign="center"
              variant="subtitle1"
              sx={{
                fontSize: isSmallScreen ? '0.675rem' : '1rem',
              }}
            >
              {isNotFound
                ? t('pages.ticketDetails.noUsersFound')
                : t('pages.ticketDetails.noUserAvailable')}
            </Typography>
          )}
        </List>
      </Card>
      <FormControl>
        <InputLabel
          id="ticket-status-select-label"
          sx={{
            fontSize: isSmallScreen ? '0.675rem' : '1rem',
          }}
        >
          {t('pages.ticketDetails.permission')}
        </InputLabel>
        <Select
          labelId="ticket-status-select-label"
          id="status-select"
          name="status_id"
          label="Permission"
          value={selectedPermission}
          onChange={handlePermissionChange}
          size={isSmallScreen ? 'small' : 'medium'}
          MenuProps={{
            PaperProps: {
              sx: {
                ...scrollbarStyle,
              },
            },
          }}
        >
          <MenuItem
            value="read"
            sx={{
              fontSize: isSmallScreen ? '0.675rem' : '1rem',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
            >
              {t('pages.ticketDetails.read')}
            </Typography>
          </MenuItem>
          <MenuItem
            value="write"
            sx={{
              fontSize: isSmallScreen ? '0.675rem' : '1rem',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
            >
              {t('pages.ticketDetails.modify')}
            </Typography>
          </MenuItem>
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        onClick={handleAddPermissionClick}
        disabled={selectedUsers.length === 0}
        sx={{
          fontSize: isSmallScreen ? '0.675rem' : '1rem',
        }}
      >
        {t('pages.ticketDetails.addUserPermission')}
      </Button>
    </Stack>
  );
}
